import { apiClient } from '../utils/api';

export interface ItemCollection {
  id: string;
  code: string;
  name: string;
  description?: string;
  isActive?: boolean;
  bodyZoneCount?: number;
  piercingTypeCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ItemCollectionsListResponse {
  itemCollections: ItemCollection[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

async function parseJson(response: Response): Promise<any> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${response.status})`);
  }
  return data;
}

export async function listItemCollections(params?: {
  page?: number;
  limit?: number;
  search?: string;
  sortKey?: string;
  sortDir?: string;
}): Promise<ItemCollectionsListResponse> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.search) search.set('search', params.search);
  if (params?.sortKey) search.set('sortKey', params.sortKey);
  if (params?.sortDir) search.set('sortDir', params.sortDir);
  const qs = search.toString();
  const response = await apiClient(`/api/admin/item-collections${qs ? `?${qs}` : ''}`);
  const data = await parseJson(response);
  return data.data;
}

export async function createItemCollection(payload: {
  code: string;
  name: string;
  description?: string;
}): Promise<{ id: string }> {
  const response = await apiClient('/api/admin/item-collections', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseJson(response);
  return data.data;
}

export async function updateItemCollection(
  id: string,
  payload: { code?: string; name?: string; description?: string; isActive?: boolean },
): Promise<void> {
  const response = await apiClient(`/api/admin/item-collections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  await parseJson(response);
}

export async function deleteItemCollection(id: string, hardDelete = false): Promise<void> {
  const response = await apiClient(
    `/api/admin/item-collections/${id}${hardDelete ? '?hardDelete=true' : ''}`,
    { method: 'DELETE' },
  );
  await parseJson(response);
}

export async function assignBodyZonesToCollection(collectionId: string, bodyZoneIds: string[]): Promise<void> {
  const response = await apiClient(`/api/admin/item-collections/${collectionId}/body-zones`, {
    method: 'PUT',
    body: JSON.stringify({ bodyZoneIds }),
  });
  await parseJson(response);
}

export async function assignPiercingTypesToCollection(
  collectionId: string,
  piercingTypeIds: string[],
): Promise<void> {
  const response = await apiClient(`/api/admin/item-collections/${collectionId}/piercing-types`, {
    method: 'PUT',
    body: JSON.stringify({ piercingTypeIds }),
  });
  await parseJson(response);
}
